import { cva } from "class-variance-authority";

// Re-export shared form CVAs for backward compatibility
export {
  formErrorMessageCva as errorMessageCva,
  formLabelCva as labelCva,
  formRequiredCva as requiredCva,
  formSubWrapperCva as subWrapperCva,
  formWrapperCva as wrapperCva,
} from "../Form/form.cva";

// Input-specific CVAs

export const inputCva = cva(
  "relative flex items-center rounded-[25px] justify-between w-full border border-(--input-border)",
  {
    variants: {
      variant: {
        "is-gray": "bg-(--input-background)",
        "is-white": "bg-(--input-background)",
        // Blends into its surface (a sidebar, a toolbar) until it is hovered, focused or filled in.
        "is-transparent": "bg-transparent transition-colors hover:bg-(--input-background) focus-within:bg-(--input-background) has-[input:not(:placeholder-shown)]:bg-(--input-background)",
      },
      textAlign: {
        "is-text-align-left": null,
        "is-text-align-center": null,
        "is-text-align-right": null,
      },
      size: {
        medium: "h-8",
        large: "h-10",
      },
      hasTopLabel: {
        true: null,
        false: null,
      },
      hasIconLeft: {
        true: null,
        false: null,
      },
      hasIconRight: {
        true: null,
        false: null,
      },
      // Focus shows a primary border on every input; an invalid field keeps its red border while focused.
      hasValidationError: {
        true: "border-danger-600",
        false: "focus-within:border-primary-500",
      },
      // A value the app checked and accepted, such as a reservation number that was found.
      tone: {
        default: null,
        success: "border-primary-500",
      },
    },
    compoundVariants: [
      {
        variant: ["is-gray", "is-white", "is-transparent"],
        hasValidationError: true,
        class: "border-danger-600",
      },
      {
        textAlign: "is-text-align-right",
        class: "[&_input]:text-right",
      },
      {
        textAlign: "is-text-align-left",
        class: "[&_input]:text-left",
      },
      {
        textAlign: "is-text-align-center",
        class: "[&_input]:text-center",
      },
      {
        hasIconLeft: true,
        class: "[&_input]:pl-[37px]",
      },
      {
        hasIconRight: true,
        class: "[&_input]:pr-[40px]",
      },
      {
        variant: ["is-gray", "is-white", "is-transparent"],
        class: "[&_input]:bg-transparent [&_input]:text-(--input-text) [&_input]:placeholder:text-(--input-placeholder) [&_.icon]:text-(--text)",
      },
      {
        variant: ["is-gray", "is-white", "is-transparent"],
        hasValidationError: true,
        class: "[&_input]:text-danger-600 [&_input]:placeholder:text-danger-600",
      },
    ],
    defaultVariants: {
      variant: "is-gray",
      textAlign: "is-text-align-left",
      size: "medium",
      hasTopLabel: false,
      hasIconLeft: false,
      hasIconRight: false,
      hasValidationError: false,
      tone: "default",
    },
  },
);

export const inputContentCva = cva(
  "flex-1 border-none text-inherit box-content w-full h-full pl-5 focus:outline-hidden rounded-[25px]",
  {
    variants: {
      size: {
        medium: "text-base sm:text-sm leading-8",
        large: "text-base sm:text-sm leading-10",
      },
    },
    defaultVariants: {
      size: "medium",
    },
  },
);

export const iconLeftCva = cva("absolute text-sm inline-flex items-center left-0 ml-[15px] text-gray-800 dark:text-gray-200");

export const iconRightCva = cva("absolute text-sm inline-flex items-center right-0 mr-[15px] text-gray-800 dark:text-gray-200", {
  variants: {
    clearable: {
      true: "cursor-pointer",
      false: null,
    },
    hasAction: {
      true: "cursor-pointer",
      false: null,
    },
  },
  defaultVariants: {
    clearable: false,
    hasAction: false,
  },
});

/** The status line under a field: what a lookup found, announced politely. */
export const statusMessageCva = cva("mt-1.5 flex items-center gap-1.5 text-xs leading-4 font-medium", {
  variants: {
    tone: {
      default: "text-gray-600 dark:text-gray-300",
      success: "text-primary-700 dark:text-primary-300",
    },
  },
  defaultVariants: {
    tone: "default",
  },
});
